import React, { useEffect, useRef, useState } from "react";
import {
  webLoadingFalse,
  webNavigate,
} from "../../services/navigate.service";
import EditIconV2 from "../../images/EditIconV2";
import DeleteIconV2 from "../../images/DeleteIconV2";
import DuplicateIcon from "../../images/DuplicateIcon";
import ViewIcon from "../../images/ViewIcon";
import { deleteRole, getRoles } from "../../services/roles.service";
import { useResponsive } from "../../hook/useResponsive";
import { normalizeApiError, showApiError } from "../../services/apiError";
import { showToast } from "../../services/toast.service";
import {
  ListColumn,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  TEXT,
  TEXT_DIM,
} from "../../components/list";
import { useAccessContext } from "../../hook/useAccessContext";
import { canEditWebRole } from "../../services/accessContext";
import { Badge, NUM, RoleDialog } from "./roleShared";
import { deleteHoldersKey, implicitAllModules, isSystemRole } from "./roleLogic";

interface RoleRow {
  id: string;
  roleName: string;
  roleDescription: string;
  roleScope?: string;
  userCount?: number;
  moduleCount?: number;
  totalModuleCount?: number;
  modulesImplicitAll?: boolean;
  isSystemRole?: boolean;
  loadModuleName?: string | null;
  canEdit?: boolean;
  isOwnRole?: boolean;
}
interface RolesResponse {
  data: {
    data: RoleRow[];
    count: number;
  };
}
export interface RolesProps {
  /** ListRole's tab, kept in the URL while paging and searching. */
  tab?: string;
  /** 'support' lists InnovX support roles; omitted, the roles this company may see. */
  scope?: "support";
}

interface DeleteState {
  role: RoleRow;
  /** Set once the API refused: holders across every company, link and staff grant. */
  blockedCount?: number;
  busy?: boolean;
}

const Roles = ({ tab, scope }: RolesProps) => {
  const { isTouchLayout } = useResponsive();
  const access = useAccessContext();
  const preLanguage = localStorage.getItem("language");
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<ListScreenHandle>(null);
  const [deleting, setDeleting] = useState<DeleteState | null>(null);
  const tr = (key: string) => (language && language[key] ? language[key] : key);
  const fill = (key: string, values: Record<string, string | number>) =>
    Object.keys(values).reduce((text, name) => text.split(`{${name}}`).join(String(values[name])), tr(key));
  const support = scope === "support";
  const emptyState = support
    ? { title: tr("No support roles yet"), desc: tr("Support roles set what InnovX staff can do inside customer companies.") }
    : {
        title: tr("No web roles yet"),
        desc: tr("Create roles to manage user access and permissions within the system."),
      };

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  /** One page of roles; the API matches `searchValue` on name and description. */
  const fetchRoles = async (query: ListQuery) => {
    webLoadingFalse();
    const response: RolesResponse = await getRoles({
      page: query.page,
      limit: query.limit,
      searchValue: query.search.trim(),
      ...(scope ? { scope } : {}),
    });
    return {
      rows: response.data.data ?? [],
      count: response.data.count ?? 0,
    };
  };

  const goToRole = (id: string) => webNavigate("/" + preLanguage + "/roles/" + encodeURIComponent(id));
  const duplicateRole = (id: string) =>
    webNavigate(
      "/" + preLanguage + "/roles/new?copyFrom=" + encodeURIComponent(id) + (support ? "&scope=support" : ""),
    );

  const confirmDelete = async () => {
    if (!deleting || deleting.busy) return;
    const role = deleting.role;
    setDeleting({ ...deleting, busy: true });
    try {
      await deleteRole(role.id);
      setDeleting(null);
      showToast({ message: fill("{name} deleted", { name: role.roleName }), type: "success", duration: 4000 });
      listRef.current?.reload();
    } catch (error: any) {
      const normalized = normalizeApiError(error);
      if (normalized.status === 409) {
        const held = Number(error?.response?.data?.userCount);
        setDeleting({ role, blockedCount: Number.isFinite(held) ? held : role.userCount ?? 0 });
      } else {
        setDeleting({ role });
        showApiError(error, { language });
      }
    }
  };

  const usersLabel = (n: number) => (n === 1 ? tr("1 user") : fill("{n} users", { n }));
  const accessLabel = (r: RoleRow) => {
    const on = Number(r.moduleCount ?? 0);
    const total = Number(r.totalModuleCount ?? 0);
    // No saved pages means every page at sign-in, not none.
    if (implicitAllModules(r, on, total)) return tr("All (not saved)");
    if (total > 0 && on >= total) return fill("All {m} modules", { m: total });
    return fill("{n} of {m}", { n: on, m: total });
  };
  const scopeBadge = (r: RoleRow) =>
    !support && r.roleScope === "support" ? (
      <Badge label={tr("InnovX support")} kind="teal" />
    ) : !support && r.roleScope === "platform" ? (
      <Badge label={tr("InnovX only")} kind="teal" />
    ) : null;

  const columns: ListColumn<RoleRow>[] = [
    {
      key: "roleName",
      label: "Role",
      width: 320,
      hideable: false,
      render: (r) => (
        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <span style={{ fontSize: "14px", fontWeight: 500, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.roleName}
            </span>
            {r.isOwnRole && <Badge label={tr("Your role")} kind="ok" />}
            {scopeBadge(r)}
          </span>
          {r.roleDescription && (
            <span style={{ fontSize: "12.5px", color: TEXT_DIM, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.roleDescription}
            </span>
          )}
        </div>
      ),
      mobileRender: (r) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.roleName}</span>
          {r.isOwnRole && <Badge label={tr("Your role")} kind="ok" />}
        </span>
      ),
      value: (r) => r.roleName ?? "",
      mobile: "title",
      onClick: (r) => goToRole(String(r.id)),
    },
    {
      key: "roleDescription",
      label: "Description",
      width: 1,
      desktop: false,
      value: (r) => r.roleDescription ?? "",
      mobile: "subtitle",
    },
    {
      key: "userCount",
      label: "Users",
      width: 100,
      value: (r) => usersLabel(Number(r.userCount ?? 0)),
      render: (r) => <span style={{ fontSize: "14px", color: TEXT, ...NUM }}>{Number(r.userCount ?? 0)}</span>,
      mobile: "trailing",
      mobileRender: (r) => <span style={{ fontSize: "13px", color: TEXT_DIM, ...NUM }}>{usersLabel(Number(r.userCount ?? 0))}</span>,
    },
    {
      key: "access",
      label: "Access",
      width: 160,
      value: (r) => accessLabel(r),
      render: (r) => <span style={{ fontSize: "14px", color: TEXT, ...NUM }}>{accessLabel(r)}</span>,
      mobile: "sheet",
    },
    {
      key: "loadModuleName",
      label: "Lands on",
      width: 200,
      value: (r) => (r.loadModuleName ? tr(r.loadModuleName) : "—"),
      mobile: "sheet",
    },
  ];

  const editable = (r: RoleRow) => canEditWebRole(access, r);
  // Without edit rights rows only open read-only; the API refuses every write anyway.
  const rowActions: ListRowAction<RoleRow>[] = access.canEditRoles
    ? [
        {
          key: "edit",
          label: "Edit",
          icon: <EditIconV2 />,
          sheetIcon: "chev",
          hidden: (r) => !editable(r),
          onRun: (r) => goToRole(String(r.id)),
        },
        {
          key: "view",
          label: "View",
          icon: <ViewIcon />,
          sheetIcon: "chev",
          hidden: (r) => editable(r),
          onRun: (r) => goToRole(String(r.id)),
        },
        {
          key: "duplicate",
          label: "Duplicate",
          icon: <DuplicateIcon />,
          sheetIcon: "plus",
          onRun: (r) => duplicateRole(String(r.id)),
        },
        {
          key: "delete",
          label: "Delete",
          icon: <DeleteIconV2 />,
          sheetIcon: "close",
          danger: true,
          hidden: (r) => !editable(r) || isSystemRole(r),
          onRun: (r) => setDeleting({ role: r }),
        },
      ]
    : [
        {
          key: "view",
          label: "View",
          icon: <ViewIcon />,
          sheetIcon: "chev",
          onRun: (r) => goToRole(String(r.id)),
        },
      ];

  const heldHere = Number(deleting?.role.userCount ?? 0);
  const blocked = deleting?.blockedCount !== undefined || heldHere > 0;
  const deleteBody = deleting && (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {deleting.blockedCount !== undefined ? (
        <p role="alert" style={{ margin: 0, color: "#BA0E13", fontWeight: 500 }}>
          {fill("{name} is still held by {n} users or staff across all companies. Move them to another role first.", {
            name: deleting.role.roleName,
            n: deleting.blockedCount,
          })}
        </p>
      ) : (
        <p style={{ margin: 0 }}>
          {fill(deleteHoldersKey(deleting.role, heldHere), { n: heldHere, name: deleting.role.roleName })}
        </p>
      )}
      {(deleting.role.roleScope ?? "tenant") === "tenant" && (
        <p style={{ margin: 0 }}>{tr("Web roles are shared by every company, so this removes it everywhere.")}</p>
      )}
    </div>
  );

  return (
    <>
      <div
        className="h-full relative"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: isTouchLayout ? "auto" : undefined,
          paddingBottom: isTouchLayout ? "24px" : undefined,
        }}
      >
        <ListScreen
          ref={listRef}
          language={language}
          columns={columns}
          fetchRows={fetchRoles}
          searchPlaceholder="Search roles"
          searchInputRef={searchInputRef}
          rowActions={rowActions}
          countNoun="roles"
          emptyState={emptyState}
          extraUrlParams={tab ? { tab } : undefined}
        />
      </div>
      <RoleDialog
        open={!!deleting}
        touch={isTouchLayout}
        language={language}
        title={deleting ? fill("Delete {name}?", { name: deleting.role.roleName }) : ""}
        subtitle={deleting ? usersLabel(heldHere) : undefined}
        onClose={() => setDeleting(null)}
        actions={[
          { label: tr("Cancel"), onClick: () => setDeleting(null) },
          {
            label: tr("Delete"),
            variant: "danger",
            disabled: blocked || !!deleting?.busy,
            onClick: confirmDelete,
          },
        ]}
      >
        {deleteBody}
      </RoleDialog>
    </>
  );
};

export default Roles;
